import React, { useRef, useMemo, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, Icon, Grid, DateInput, cn, Heading, Text } from '@tasama/ui';
import { useTranslation } from '@tasama/i18n';
import {
  createTravelDateSchema,
  normalizeDate,
  addWorkingDays,
  createIsWeekend,
  MIN_STAY_DAYS,
  type TravelDateFormData,
} from '../schemas/travel-date.schema';
import type { HolidayItem } from '@/features/my-services/business-landing/hooks';

interface AddTravelDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (_dates: { fromDate: string; toDate: string }) => Promise<void>;
  fromDate?: string;
  toDate?: string;
  isLoading?: boolean;
  holidays?: HolidayItem[];
  testID?: string;
}

export const AddTravelDateModal: React.FC<AddTravelDateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  fromDate: initialFromDate = '',
  toDate: initialToDate = '',
  isLoading = false,
  holidays = [],
  testID = 'add-travel-date-modal',
}) => {
  const { t, i18n } = useTranslation('portal');
  const isWeekend = createIsWeekend(i18n.language);

  const holidayDates = useMemo(
    () =>
      holidays.map((h) => {
        const parts = h.date.split('-');
        return new Date(
          Number(parts[0]),
          Number(parts[1]) - 1,
          Number(parts[2])
        );
      }),
    [holidays]
  );

  const isHoliday = useCallback(
    (date: Date): boolean =>
      holidayDates.some(
        (h) =>
          h.getFullYear() === date.getFullYear() &&
          h.getMonth() === date.getMonth() &&
          h.getDate() === date.getDate()
      ),
    [holidayDates]
  );

  const holidayModifiers = useMemo(() => ({ holiday: isHoliday }), [isHoliday]);

  const holidayModifierClassNames = useMemo(
    () => ({
      holiday:
        '[&>button]:!bg-bg-error-primary [&>button]:!text-fg-error-primary [&>button]:!opacity-100 [&>button]:cursor-not-allowed [&>button]:pointer-events-none',
    }),
    []
  );

  type SchemaT = (_key: string, _options?: Record<string, unknown>) => string;
  const schemaRef = useRef(
    createTravelDateSchema(t as SchemaT, i18n.language, isHoliday)
  );
  schemaRef.current = createTravelDateSchema(
    t as SchemaT,
    i18n.language,
    isHoliday
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isValid },
  } = useForm<TravelDateFormData>({
    resolver: (values, context, options) =>
      zodResolver(schemaRef.current)(values, context, options),
    mode: 'onChange',
    defaultValues: {
      fromDate: initialFromDate,
      toDate: initialToDate,
    },
  });

  const [watchedFromDate, watchedToDate] = watch(['fromDate', 'toDate']);

  const handleCancel = () => {
    reset({ fromDate: initialFromDate, toDate: initialToDate });
    onClose();
  };

  const onValidSubmit = async (data: TravelDateFormData) => {
    await onSubmit(data);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const calendarStartMonth = today;
  const calendarEndMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 24,
    1
  );

  const twoWeeksFromFromDate = new Date(
    addWorkingDays(
      watchedFromDate ? normalizeDate(watchedFromDate) : today.getTime(),
      MIN_STAY_DAYS,
      isWeekend,
      isHoliday
    )
  );

  // Always reset To to the new minimum whenever From changes
  useEffect(() => {
    if (!watchedFromDate) return;
    const d = twoWeeksFromFromDate;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    setValue('toDate', `${dd}/${mm}/${d.getFullYear()}`, {
      shouldValidate: true,
    });
    // twoWeeksFromFromDate is derived from watchedFromDate — depend only on fromDate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFromDate]);

  // Range display in To calendar
  const fromDateObj = useMemo(
    () => (watchedFromDate ? new Date(normalizeDate(watchedFromDate)) : null),
    [watchedFromDate]
  );

  const isRangeStart = useCallback(
    (date: Date): boolean => {
      if (!fromDateObj) return false;
      return (
        date.getFullYear() === fromDateObj.getFullYear() &&
        date.getMonth() === fromDateObj.getMonth() &&
        date.getDate() === fromDateObj.getDate()
      );
    },
    [fromDateObj]
  );

  const toCalendarModifiers = useMemo(
    () => ({ holiday: isHoliday, rangeStart: isRangeStart }),
    [isHoliday, isRangeStart]
  );

  const toCalendarModifierClassNames = useMemo(
    () => ({
      holiday:
        '[&>button]:!bg-bg-error-primary [&>button]:!text-fg-error-primary [&>button]:!opacity-100 [&>button]:cursor-not-allowed [&>button]:pointer-events-none',
      rangeStart:
        '[&>button]:!bg-brand-blue-100 [&>button]:!text-brand-blue-700',
    }),
    []
  );

  const isSubmitDisabled =
    isLoading || !watchedFromDate || !watchedToDate || !isValid;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      closeOnOverlayClick={false}
      closeOnEscape={true}
      size={'lg'}
      testID={testID}
    >
      <Modal.Header type="left" showCloseButton={true} testID={'header'}>
        <div className={'flex-col items-start gap-4 pe-10'}>
          <div className="w-12 h-12 bg-brand-green-500 rounded-full flex items-center justify-center mb-3">
            <Icon
              name="Plus"
              className="w-6 h-6 text-white"
              testID={`${testID}-modal-icon`}
            />
          </div>
          <div className={'left'}>
            <Heading
              level={3}
              size="base"
              className="text-fg-primary leading-6 mb-1"
              testID={`${testID}-title`}
            >
              {t('addTravelDateModal.title')}
            </Heading>
            <Text
              size="sm"
              className="text-fg-tertiary leading-5"
              testID={`${testID}-description`}
            >
              {t('addTravelDateModal.description')}
            </Text>
            <Text
              size="sm"
              className="text-fg-tertiary leading-5 mt-3"
              testID={`${testID}-note`}
            >
              {t('addTravelDateModal.note')}
            </Text>
          </div>
        </div>
      </Modal.Header>
      <Modal.Content testID={`${testID}-content`}>
        <div
          className="flex items-center gap-6 mb-4"
          data-testid={`${testID}-legends`}
        >
          <span className="flex items-center gap-1.5 text-xs text-fg-tertiary leading-[18px]">
            <span className="w-2.5 h-2.5 rounded-xxs border border-fg-disabled bg-fg-disabled-subtle inline-block shrink-0" />
            {t('addTravelDateModal.weekendsLegend')}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-fg-tertiary leading-[18px]">
            <span className="w-2.5 h-2.5 rounded-xxs border border-fg-error-primary bg-bg-error-primary inline-block shrink-0" />
            {t('addTravelDateModal.publicHolidaysLegend')}
          </span>
        </div>
        <Grid className="grid-cols-1 md:grid-cols-2 gap-4">
          <Controller
            name="fromDate"
            control={control}
            render={({ field, fieldState }) => (
              <DateInput
                id="from-date"
                label={t('addTravelDateModal.fromLabel')}
                required
                value={field.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(e.target.value)
                }
                onBlur={field.onBlur}
                onMouseOut={field.onBlur}
                error={!!fieldState.error}
                errorMessage={fieldState.error?.message}
                placeholder="DD/MM/YYYY"
                className={cn(field.value ? 'date-input--has-value' : '')}
                title={t('addTravelDateModal.fromTitle')}
                disabledDays={[{ before: today }, isWeekend, isHoliday]}
                modifiers={holidayModifiers}
                modifiersClassNames={holidayModifierClassNames}
                showDropdown={true}
                startMonth={calendarStartMonth}
                endMonth={calendarEndMonth}
                testID={`${testID}-from-date`}
              />
            )}
          />
          <Controller
            name="toDate"
            control={control}
            render={({ field, fieldState }) => (
              <DateInput
                id="to-date"
                label={t('addTravelDateModal.toLabel')}
                required
                value={field.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(e.target.value)
                }
                onBlur={field.onBlur}
                onMouseOut={field.onBlur}
                error={!!fieldState.error}
                errorMessage={fieldState.error?.message}
                placeholder="DD/MM/YYYY"
                className={cn(field.value ? 'date-input--has-value' : '')}
                title={t('addTravelDateModal.toTitle')}
                disabledDays={[
                  { before: twoWeeksFromFromDate },
                  isWeekend,
                  isHoliday,
                ]}
                modifiers={toCalendarModifiers}
                modifiersClassNames={toCalendarModifierClassNames}
                showDropdown={true}
                startMonth={calendarStartMonth}
                endMonth={calendarEndMonth}
                defaultMonth={twoWeeksFromFromDate}
                min={watchedFromDate}
                testID={`${testID}-to-date`}
              />
            )}
          />
        </Grid>
      </Modal.Content>
      <Modal.Footer
        divider
        alignment="right"
        actions={[
          {
            label: t('common:actions.cancel'),
            variant: 'tertiary',
            size: 'sm',
            onClick: handleCancel,
            testID: `${testID}-cancel-button`,
            iconPosition: 'none',
          },
          {
            label: t('common:actions.save'),
            variant: 'secondary',
            size: 'sm',
            onClick: handleSubmit(onValidSubmit),
            disabled: isSubmitDisabled,
            loading: isLoading,
            testID: `${testID}-save-button`,
            iconPosition: 'trailing',
            ctaIcon: 'CircleCheck',
          },
        ]}
        testID={`${testID}-footer`}
      />
    </Modal>
  );
};

AddTravelDateModal.displayName = 'AddTravelDateModal';

export default AddTravelDateModal;
export type { AddTravelDateModalProps };
